import React, { Fragment, useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, IconButton, MenuItem, Paper, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import iziToast from 'izitoast';
import { useNavigate } from 'react-router-dom';

import JournalLayout from '../../components/Journal/JournalLayout';
import MobileFilters from '../../components/Journal/MobileFilters';
import useIsMobile from '../../utils/useIsMobile';
import { getAcademicYears, getGroups } from '../../actions/journal';
import { PERMISSIONS, useCurrentUser } from '../../utils/currentUser';
import { internalServerError, journalGroupLink, journalSheetLink } from '../../utils/consts/pathRoutes';
import { formatDate } from '../../utils/journalFormat';
import GroupModal from './GroupModal';
import StudentSearch from './StudentSearch';

const SEMESTERS = [
  { value: '', label: 'Все' },
  { value: 1, label: '1 семестр' },
  { value: 2, label: '2 семестр' },
  { value: 3, label: '3 семестр' },
];
const TYPES = [{ value: '', label: 'Все' }, { value: 'РЯ', label: 'РЯ' }, { value: 'ПО', label: 'ПО' }];
const STATUSES = ['', 'Набор', 'Активна', 'Завершена', 'Архив'];

const statusColor = {
  'Набор': 'info',
  'Активна': 'success',
  'Завершена': 'default',
  'Архив': 'default',
};

/** Текущий учебный год: до августа считается прошлый (учебный год идёт с сентября). */
const currentAcademicYear = () => {
  const now = new Date();
  const start = now.getMonth() >= 8 ? now.getFullYear() : now.getFullYear() - 1;
  return `${start}-${start + 1}`;
};

/**
 * «Списки групп» — корневой рабочий экран.
 *
 * Главный сценарий заказчика: пришёл официальный запрос, нужно быстро найти слушателя
 * и его куратора. Поэтому поиск идёт по номеру группы и ФИО куратора, а состав группы
 * не выгружается — только количество занятых мест.
 */
const GroupsPage = () => {
  const navigate = useNavigate();
  const { can } = useCurrentUser();
  const canManage = can(PERMISSIONS.JOURNAL_MANAGE);
  const isMobile = useIsMobile();

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [years, setYears] = useState([]);
  const [filters, setFilters] = useState({
    academic_year: currentAcademicYear(),
    semester: '',
    type: '',
    status: '',
  });

  const load = () => {
    setLoading(true);
    getGroups(filters)
      .then(setGroups)
      .catch((e) => iziToast.error({ message: internalServerError(e), position: 'topRight' }))
      .finally(() => setLoading(false));
  };

  useEffect(load, [filters]);

  useEffect(() => {
    getAcademicYears()
      .then((items) => {
        setYears(items);
        // Если года из фильтра нет в списке, берём самый свежий — иначе выпадающий
        // список покажется пустым, а таблица останется без данных.
        if (items.length && !items.includes(filters.academic_year)) {
          setFilters((prev) => ({ ...prev, academic_year: items[0] }));
        }
      })
      .catch(() => setYears([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totals = useMemo(() => ({
    groups: groups.length,
    students: groups.reduce((sum, group) => sum + group.students_count, 0),
  }), [groups]);

  const handleFilter = (field) => (event) =>
    setFilters((prev) => ({ ...prev, [field]: event.target.value }));

  // Учебный год выбран всегда, поэтому в счётчик и сброс он не входит.
  const activeFilters = [
    filters.semester ? `${filters.semester} семестр` : null,
    filters.type || null,
    filters.status || null,
  ].filter(Boolean);

  const resetFilters = () => setFilters((prev) => ({ ...prev, semester: '', type: '', status: '' }));

  // В панели фильтров на телефоне поля идут столбиком во всю ширину.
  const fieldProps = (width) => (isMobile ? { fullWidth: true } : { sx: { width } });

  const filterFields = (
    <Fragment>
      <TextField
        select size="small" label="Учебный год" value={filters.academic_year}
        onChange={handleFilter('academic_year')} {...fieldProps(160)}
      >
        {years.map((year) => (
          <MenuItem key={year} value={year}>{year}</MenuItem>
        ))}
      </TextField>
      <TextField
        select size="small" label="Семестр" value={filters.semester}
        onChange={handleFilter('semester')} {...fieldProps(140)}
      >
        {SEMESTERS.map((item) => <MenuItem key={item.label} value={item.value}>{item.label}</MenuItem>)}
      </TextField>
      <TextField
        select size="small" label="Тип" value={filters.type}
        onChange={handleFilter('type')} {...fieldProps(110)}
      >
        {TYPES.map((item) => <MenuItem key={item.label} value={item.value}>{item.label}</MenuItem>)}
      </TextField>
      <TextField
        select size="small" label="Статус" value={filters.status}
        onChange={handleFilter('status')} {...fieldProps(150)}
      >
        {STATUSES.map((status) => (
          <MenuItem key={status || 'all'} value={status}>{status || 'Все'}</MenuItem>
        ))}
      </TextField>
    </Fragment>
  );

  /**
   * Группы на телефоне — карточками. Комментарий выведен текстом: подсказка по наведению
   * на телефоне открывается только долгим нажатием, о котором никто не догадается.
   */
  const renderCards = () => (
    <div className="journal-cards">
      {groups.length === 0 && (
        <Paper variant="outlined" className="journal-card journal-muted" sx={{ textAlign: 'center' }}>
          Групп не найдено
        </Paper>
      )}

      {groups.map((group) => (
        <Paper
          key={group.id} variant="outlined"
          className="journal-card journal-card--clickable"
          onClick={() => navigate(journalGroupLink(group.id))}
        >
          <div className="journal-card__head">
            <div>
              <div className="journal-card__title">{group.name}</div>
              <div className="journal-card__meta">
                {group.semester} семестр · {group.curator
                  ? group.curator.name
                  : <span style={{ color: '#b26a00' }}>куратор не назначен</span>}
              </div>
            </div>
            <Chip size="small" label={group.status} color={statusColor[group.status] || 'default'} />
          </div>

          {group.comment && (
            <div className="journal-muted" style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>
              {group.comment}
            </div>
          )}

          <div className="journal-card__row">
            <Chip
              size="small"
              label={`Мест: ${group.students_count} / ${group.capacity}`}
              color={group.free_places === 0 ? 'warning' : 'default'}
            />
            {group.is_distant && <Chip size="small" variant="outlined" label="дистант" />}
            {group.is_paid && <Chip size="small" variant="outlined" label="платная" />}
            {group.opened_at && (
              <span className="journal-muted">открыта {formatDate(group.opened_at)}</span>
            )}
          </div>

          <div className="journal-card__actions">
            {canManage && (
              <Button
                size="small" startIcon={<EditIcon />}
                onClick={(event) => {
                  event.stopPropagation();
                  setEditing(group);
                }}
              >
                Изменить
              </Button>
            )}
            <Button
              size="small" startIcon={<MenuBookIcon />}
              onClick={(event) => {
                event.stopPropagation();
                navigate(journalSheetLink(group.id));
              }}
            >
              Журнал
            </Button>
          </div>
        </Paper>
      ))}
    </div>
  );

  return (
    <JournalLayout activeTab="groups">
      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <h2 className="journal-title">Списки групп</h2>
          {canManage && (
            <Tooltip title="Создать группу">
              <IconButton color="primary" onClick={() => setModalOpen(true)}>
                <AddIcon />
              </IconButton>
            </Tooltip>
          )}
        </Stack>

        <StudentSearch />

        {isMobile ? (
          <MobileFilters
            summary={[filters.academic_year, ...activeFilters].filter(Boolean).join(' · ')}
            activeCount={activeFilters.length}
            onReset={resetFilters}
          >
            {filterFields}
          </MobileFilters>
        ) : (
          <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
            {filterFields}
          </Stack>
        )}

        {loading ? (
          <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
        ) : (
          <Fragment>
            <div className="journal-muted" style={{ marginBottom: 8 }}>
              Групп: {totals.groups} · слушателей: {totals.students}
            </div>

            {isMobile ? renderCards() : (
              <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Группа</TableCell>
                      <TableCell>Семестр</TableCell>
                      <TableCell>Куратор</TableCell>
                      <TableCell align="center">Мест занято</TableCell>
                      <TableCell>Статус</TableCell>
                      <TableCell>Открыта</TableCell>
                      <TableCell>Признаки</TableCell>
                      <TableCell align="center">{canManage ? 'Действия' : 'Журнал'}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groups.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                          Групп не найдено
                        </TableCell>
                      </TableRow>
                    )}

                    {groups.map((group) => (
                      <TableRow
                        key={group.id} hover sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(journalGroupLink(group.id))}
                      >
                        {/* Комментарий по группе — в подсказке: в строке для него нет места,
                            а знать, что РЯ-3 набрана в Даляне, нужно прямо здесь. Пунктир
                            под названием подсказывает, что наводить есть смысл. */}
                        <TableCell>
                          {group.comment ? (
                            <Tooltip title={group.comment}>
                              <b style={{ borderBottom: '1px dotted #9e9e9e', cursor: 'help' }}>
                                {group.name}
                              </b>
                            </Tooltip>
                          ) : <b>{group.name}</b>}
                        </TableCell>
                        <TableCell>{group.semester}</TableCell>
                        <TableCell>
                          {group.curator
                            ? group.curator.name
                            : <span style={{ color: '#b26a00' }}>не назначен</span>}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={`${group.students_count} / ${group.capacity}`}
                            color={group.free_places === 0 ? 'warning' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={group.status} color={statusColor[group.status] || 'default'} />
                        </TableCell>
                        <TableCell>{formatDate(group.opened_at) || '—'}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5}>
                            {group.is_distant && <Chip size="small" variant="outlined" label="дистант" />}
                            {group.is_paid && <Chip size="small" variant="outlined" label="платная" />}
                          </Stack>
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            {canManage && (
                              <Tooltip title="Изменить группу: мест, статус, комментарий">
                                <IconButton
                                  size="small"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setEditing(group);
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Открыть журнал группы">
                              <IconButton
                                size="small"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  navigate(journalSheetLink(group.id));
                                }}
                              >
                                <MenuBookIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Fragment>
        )}
      </Box>

      {canManage && (
        <GroupModal
          open={modalOpen || Boolean(editing)}
          group={editing}
          defaultAcademicYear={filters.academic_year}
          onClose={() => { setModalOpen(false); setEditing(null); }}
          onSaved={() => { setModalOpen(false); setEditing(null); load(); }}
        />
      )}
    </JournalLayout>
  );
};

export default GroupsPage;
